<section aria-label="Filter presets" class="flex flex-col gap-3">
  <h3 class="text-sm font-semibold font-header text-gray-900 dark:text-white">My Saved Searches</h3>

  @if (filteringService.userSavedSearches().length > 0) {
    <div class="flex items-center gap-2">
      <app-select class="flex-1 min-w-0" [(value)]="activeId" [options]="presetOptions()" />
      @if (activeId()) {
        <button
          type="button"
          class="shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-red-400 cursor-pointer"
          aria-label="Delete saved search"
          (click)="deleteActive()"
        >
          <svg class="size-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fill-rule="evenodd"
              d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      }
    </div>
  }

  @if (filteringService.userSavedSearches()?.length === 0 && !isSaving()) {
    <p class="text-xs text-gray-500 dark:text-gray-400 leading-snug">
      Set your filters, then save them here to reapply in one tap.
    </p>
  }

  @if (isSaving() || filteringService.hasFilters()) {
    <!-- Inline save form -->
    <div
      class="grid transition-[grid-template-rows] duration-200 ease-in-out"
      [style.grid-template-rows]="isSaving() || filteringService.hasFilters() ? '1fr' : '0fr'"
    >
      <div class="overflow-hidden">
        @if (isSaving()) {
          <div
            class="flex flex-col gap-2"
            role="group"
            aria-label="Save current filters as a preset"
          >
            <label for="save-filter-preset-name" class="text-xs font-medium text-gray-400">
              Name this search
            </label>
            <input
              #nameInput
              id="save-filter-preset-name"
              type="text"
              class="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary-500 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-white/30 dark:focus:outline-primary-400"
              [value]="saveName()"
              // eslint-disable-next-line @angular-eslint/template/no-any -- Using $any to bypass strict type checking for the input event target.
              (input)="saveName.set($any($event.target).value)"
              (keydown.enter)="confirmSave()"
              (keydown.escape)="cancelSave()"
              maxlength="30"
              autocomplete="off"
              aria-describedby="save-preset-hint save-preset-count"
            />
            <div class="flex justify-between items-center">
              <span id="save-preset-hint" class="sr-only"
                >Press Enter to save or Escape to cancel</span
              >
              <span
                id="save-preset-count"
                [class]="
                  saveName().length >= 25
                    ? 'text-xs tabular-nums ml-auto text-red-500 dark:text-red-400'
                    : 'text-xs tabular-nums ml-auto text-gray-400 dark:text-gray-500'
                "
                aria-live="polite"
                aria-atomic="true"
                >{{ saveName().length }}/30</span
              >
            </div>
            <div class="flex gap-2">
              <button
                type="button"
                class="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-400 disabled:opacity-40 cursor-pointer"
                [disabled]="saveName().trim().length === 0"
                (click)="confirmSave()"
              >
                Save preset
              </button>
              <button
                type="button"
                class="rounded-md px-4 py-2.5 text-sm font-semibold text-gray-600 transition-colors hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 dark:text-white/60 dark:hover:text-white dark:focus-visible:outline-white/30 cursor-pointer"
                (click)="cancelSave()"
              >
                Cancel
              </button>
            </div>
          </div>

          <!-- Save button — only visible when filters are active -->
        } @else if (filteringService.hasFilters() && !filteringService.selectedSavedSearchId()) {
          <button
            type="button"
            class="flex items-center gap-1.5 rounded-full py-1 text-sm text-gray-500 transition-colors hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 dark:text-white/60 dark:hover:text-white dark:focus-visible:outline-white/30 cursor-pointer"
            (click)="startSaving()"
            aria-label="Save the current combination of filters as a reusable preset"
          >
            <svg
              class="size-3.5 shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"
              />
            </svg>
            Save these filters as a preset
          </button>
        }
      </div>
    </div>
  }
</section>
